import React, { useState } from 'react';
import './ROICalculator.css';

const ROICalculator = () => {
  const [formData, setFormData] = useState({
    monthlyRevenue: 5000,
    productsCount: 50,
    dailySales: 10,
    employeesCount: 1,
    hoursPerWeek: 10
  });

  const [showResults, setShowResults] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: parseFloat(value) || 0
    });
  };

  const calculateROI = () => {
    const { monthlyRevenue, productsCount, dailySales, employeesCount, hoursPerWeek } = formData;

    // Tempo perdido com planilhas (horas/mês)
    const timeWastedPerMonth = hoursPerWeek * 4.33; // 4.33 semanas em um mês

    // Custo do tempo perdido (R$ 25/hora média)
    const hourlyRate = 25;
    const timeWastedCost = timeWastedPerMonth * hourlyRate;

    // Erros em planilhas (estimativa de 5% do faturamento em erros/retrabalho)
    const errorCost = monthlyRevenue * 0.05;

    // Falta de controle de estoque (estimativa de 3% de perda)
    const stockLossCost = monthlyRevenue * 0.03;

    // Oportunidades perdidas (sem dados, dificulta crescimento - estimativa)
    const opportunityCost = monthlyRevenue * 0.02;

    // Custo total mensal sem o sistema
    const totalMonthlyCost = timeWastedCost + errorCost + stockLossCost + opportunityCost;

    // Custo do MicroGestor
    let planCost = 0;
    let planName = 'Básico (Grátis)';

    if (productsCount > 200 || employeesCount > 3) {
      planCost = 49.90;
      planName = 'Premium';
    } else if (productsCount > 50 || employeesCount > 1) {
      planCost = 29.90;
      planName = 'Profissional';
    }

    // Economia mensal
    const monthlySavings = totalMonthlyCost - planCost;

    // Economia anual
    const annualSavings = monthlySavings * 12;

    // ROI (retorno sobre investimento)
    const roi = planCost > 0 ? ((monthlySavings / planCost) * 100) : Infinity;

    // Tempo economizado
    const timeSaved = timeWastedPerMonth;

    return {
      totalMonthlyCost,
      planCost,
      planName,
      monthlySavings,
      annualSavings,
      roi,
      timeSaved,
      timeWastedCost,
      errorCost,
      stockLossCost,
      opportunityCost
    };
  };

  const results = calculateROI();

  const handleCalculate = () => {
    setShowResults(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="roi-calculator">
      <div className="roi-header">
        <h2>
          <i className="bi bi-calculator me-2"></i>
          Calculadora de Economia
        </h2>
        <p>Descubra quanto você está perdendo sem um sistema de gestão</p>
      </div>

      <div className="roi-content">
        <div className="roi-form">
          <h3>Conte-nos sobre seu negócio</h3>

          <div className="form-group">
            <label htmlFor="monthlyRevenue">
              <i className="bi bi-cash-stack"></i>
              Faturamento mensal (R$)
            </label>
            <input
              type="number"
              id="monthlyRevenue"
              name="monthlyRevenue"
              className="form-control"
              value={formData.monthlyRevenue}
              onChange={handleChange}
              min="0"
              step="500"
            />
          </div>

          <div className="form-group">
            <label htmlFor="productsCount">
              <i className="bi bi-box-seam"></i>
              Quantidade de produtos
            </label>
            <input
              type="number"
              id="productsCount"
              name="productsCount"
              className="form-control"
              value={formData.productsCount}
              onChange={handleChange}
              min="1"
              step="10"
            />
          </div>

          <div className="form-group">
            <label htmlFor="dailySales">
              <i className="bi bi-cart-check"></i>
              Vendas por dia (média)
            </label>
            <input
              type="number"
              id="dailySales"
              name="dailySales"
              className="form-control"
              value={formData.dailySales}
              onChange={handleChange}
              min="1"
              step="5"
            />
          </div>

          <div className="form-group">
            <label htmlFor="employeesCount">
              <i className="bi bi-people"></i>
              Número de colaboradores
            </label>
            <input
              type="number"
              id="employeesCount"
              name="employeesCount"
              className="form-control"
              value={formData.employeesCount}
              onChange={handleChange}
              min="1"
              max="20"
            />
          </div>

          <div className="form-group">
            <label htmlFor="hoursPerWeek">
              <i className="bi bi-clock"></i>
              Horas gastas com gestão manual por semana
            </label>
            <input
              type="number"
              id="hoursPerWeek"
              name="hoursPerWeek"
              className="form-control"
              value={formData.hoursPerWeek}
              onChange={handleChange}
              min="1"
              max="60"
            />
          </div>

          <button className="btn btn-primary btn-calculate" onClick={handleCalculate}>
            <i className="bi bi-calculator"></i>
            Calcular Economia
          </button>
        </div>

        <div className={`roi-results ${showResults ? 'show' : ''}`}>
          <div className="results-hero">
            <div className="hero-value">
              <span className="label">Você economiza</span>
              <h2 className="value">{formatCurrency(results.monthlySavings)}</h2>
              <span className="period">por mês</span>
            </div>

            <div className="hero-annual">
              <i className="bi bi-calendar-check"></i>
              <div>
                <strong>{formatCurrency(results.annualSavings)}</strong>
                <span>por ano</span>
              </div>
            </div>
          </div>

          <div className="results-details">
            <h4>Detalhamento da economia:</h4>

            <div className="detail-item">
              <div className="detail-icon">
                <i className="bi bi-clock-history"></i>
              </div>
              <div className="detail-content">
                <strong>Tempo recuperado</strong>
                <p>{results.timeSaved.toFixed(0)}h/mês economizadas</p>
                <span className="detail-value">{formatCurrency(results.timeWastedCost)}</span>
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-icon">
                <i className="bi bi-exclamation-triangle"></i>
              </div>
              <div className="detail-content">
                <strong>Erros evitados</strong>
                <p>Sem retrabalho com planilhas</p>
                <span className="detail-value">{formatCurrency(results.errorCost)}</span>
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-icon">
                <i className="bi bi-box-seam"></i>
              </div>
              <div className="detail-content">
                <strong>Controle de estoque</strong>
                <p>Evite perdas e rupturas</p>
                <span className="detail-value">{formatCurrency(results.stockLossCost)}</span>
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-icon">
                <i className="bi bi-graph-up-arrow"></i>
              </div>
              <div className="detail-content">
                <strong>Oportunidades capturadas</strong>
                <p>Decisões baseadas em dados</p>
                <span className="detail-value">{formatCurrency(results.opportunityCost)}</span>
              </div>
            </div>
          </div>

          <div className="results-plan">
            <h4>Plano recomendado para você:</h4>
            <div className="plan-card">
              <div className="plan-header">
                <h5>{results.planName}</h5>
                <div className="plan-price">
                  <span className="price">{formatCurrency(results.planCost)}</span>
                  <span className="period">/mês</span>
                </div>
              </div>
              {results.roi !== Infinity && (
                <div className="plan-roi">
                  <i className="bi bi-trophy"></i>
                  <span>ROI de {results.roi.toFixed(0)}% no primeiro mês</span>
                </div>
              )}
              {results.planCost === 0 && (
                <div className="plan-free">
                  <i className="bi bi-gift"></i>
                  <span>Comece grátis, sem cartão de crédito!</span>
                </div>
              )}
            </div>
          </div>

          <div className="results-cta">
            <a href="/register" className="btn btn-success btn-lg">
              <i className="bi bi-rocket-takeoff"></i>
              Começar a economizar agora
            </a>
            <p className="cta-subtitle">Teste grátis, sem compromisso</p>
          </div>
        </div>
      </div>

      <div className="roi-disclaimer">
        <i className="bi bi-info-circle"></i>
        <small>
          Os valores são estimativas baseadas em médias de mercado.
          Resultados reais podem variar de acordo com seu negócio.
        </small>
      </div>
    </div>
  );
};

export default ROICalculator;
